import clsx from 'clsx';
import { InputText } from 'primereact/inputtext';
import {
    type DetailedHTMLProps,
    forwardRef,
    type InputHTMLAttributes,
    ReactNode,
    Ref,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { GetInputClassName } from '../../_utils/get-input-class-name';
import { getPlaceholder } from '../../_utils/get-placeholder';
import {
    buildRhfRules,
    type FieldLengthRule,
    type FieldValidate,
    lengthRuleValue,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

type HTMLProps = DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
>;

export type TextInputProps = HTMLProps &
    InputWrapperSharedProps & {
        /**
         * The input field name
         */
        name: string;
        /**
         * Input field placeholder
         */
        placeholder?: string;
        /**
         * Input field value (this input is controlled only for now)
         */
        value?: string;
        /**
         * Additional custom styles (overrides)
         */
        disabled?: boolean;
        /**
         * Define if label should be hidden
         */
        invalid?: boolean;
        /**
         * Icon to be displayed at the end of the input
         */
        suffixIcon?: ReactNode;
        /**
         * Icon to be displayed at the beginning of the input
         */
        prefixIcon?: ReactNode;
        /**
         * Debounce value change, it is used only by controlled component
         */
        debounce?: number;
        /**
         * Underline-only border appearance (bottom border only, no surrounding border)
         */
        outlined?: boolean;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
        /**
         * RHF minLength rule, only used when this input is RHF-controlled via `name`
         */
        minLength?: FieldLengthRule;
        /**
         * RHF maxLength rule, only used when this input is RHF-controlled via `name`
         */
        maxLength?: FieldLengthRule;
    };

function InputComponent(
    props: TextInputProps,
    ref: Ref<HTMLInputElement>,
): ReactNode {
    const {
        name,
        className,
        wrapperClassName,
        required,
        onFocus,
        errorMessage,
        onBlur,
        debounce,
        bulkMode,
        labelHidden: __,
        labelClassName: ___,
        horizontal: ____,
        label: _____,
        labelSuffix: ______,
        suffixIcon,
        prefixIcon,
        outlined,
        validate,
        minLength,
        maxLength,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;
    const placeholder = getPlaceholder(props);
    const formContext = useFormContext();
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const [focus, setFocus] = useState<boolean>(false);

    const suffixIconTemplate = suffixIcon ? (
        <span className="p-inputgroup-addon">{suffixIcon}</span>
    ) : null;
    const prefixIconTemplate = prefixIcon ? (
        <span className="p-inputgroup-addon">{prefixIcon}</span>
    ) : null;
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const isControlled = typeof restProps.value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'Input',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    if (!isRHFControlled) {
        return (
            <InputWrapper
                wrapperClassName={clsx(
                    className,
                    wrapperClassName,
                    outlined && 'input-wrapper--outlined',
                )}
                {...props}
            >
                <div
                    className={clsx(
                        'p-inputgroup flex-1',
                        restProps.disabled && 'p-inputgroup--disabled',
                    )}
                >
                    {prefixIconTemplate}
                    <InputText
                        ref={ref}
                        data-testid={testId}
                        {...restProps}
                        name={name}
                        minLength={lengthRuleValue(minLength)}
                        maxLength={lengthRuleValue(maxLength)}
                        onChange={(e) => {
                            const { onChange } = restProps;
                            if (!onChange) {
                                return;
                            }

                            if (!debounce) {
                                onChange(e);
                                return;
                            }

                            if (debounceTimer.current) {
                                clearTimeout(debounceTimer.current);
                            }

                            debounceTimer.current = setTimeout(
                                () => onChange(e),
                                debounce,
                            );
                        }}
                        placeholder={placeholder}
                        onBlur={onBlur}
                        className={GetInputClassName({
                            basicClassName: 'flex grow',
                            invalidState: Boolean(errorMessage),
                        })}
                    />
                    {suffixIconTemplate}
                </div>
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name}
            control={formContext!.control}
            rules={buildRhfRules({
                required,
                bulkMode,
                validate,
                minLength,
                maxLength,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper
                    field={field}
                    fieldState={fieldState}
                    focus={focus}
                    wrapperClassName={clsx(
                        wrapperClassName,
                        outlined && 'input-wrapper--outlined',
                    )}
                    {...props}
                >
                    <div
                        className={clsx(
                            'p-inputgroup flex-1',
                            restProps.disabled && 'p-inputgroup--disabled',
                        )}
                    >
                        {prefixIconTemplate}
                        <InputText
                            data-testid={testId}
                            className={GetInputClassName({
                                basicClassName: 'flex grow',
                                invalidState: fieldState.invalid,
                            })}
                            variant="outlined"
                            {...restProps}
                            {...field}
                            name={name}
                            minLength={lengthRuleValue(minLength)}
                            maxLength={lengthRuleValue(maxLength)}
                            placeholder={placeholder}
                            value={field?.value || props.value || ''}
                            ref={mergeRefs(ref, field.ref)}
                            onBlurCapture={() => setFocus(false)}
                            onChange={(e) => {
                                if (restProps.onChange) {
                                    restProps.onChange(e);
                                }

                                field.onChange(e);
                            }}
                            onFocus={(e) => {
                                setFocus(true);

                                if (onFocus) {
                                    onFocus(e);
                                }
                            }}
                            onBlur={(e) => {
                                field.onBlur();

                                if (onBlur) {
                                    onBlur(e);
                                }
                            }}
                        />
                        {suffixIconTemplate}
                    </div>
                </InputWrapper>
            )}
        />
    );
}

export const Input = forwardRef(InputComponent);
